<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
} from '@/components/ui/context-menu'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import { useGroupsStore } from '~/stores/groups'
import { getParticipantVolume } from '~/lib/settings/appSettings'

/**
 * Per-participant local audio controls (relative mic/screen-share volume, "mute for me") shared
 * between `CallTile.vue` (in an active call) and `VoiceChannelItem.vue`'s sidebar member list
 * (not necessarily in that channel at all). Everything here is keyed purely by LiveKit identity
 * (= backend user id) and reads/writes through the same persisted settings either way — the
 * controls always render and stay enabled regardless of whether the target participant currently
 * has a live LiveKit `Participant` object (connected, screen-sharing, etc.), since the setting
 * itself is just "what should happen the next time I actually hear this person".
 */
const props = defineProps<{
  identity: string
  name: string
}>()

const groupsStore = useGroupsStore()

const storedVolume = getParticipantVolume(props.identity)
const micVolume = ref(storedVolume.mic)
const screenShareVolume = ref(storedVolume.screenShare)
// Reactive (not a local ref like the volumes above) so a mute toggled here shows up on a
// `CallTile.vue` badge for the same identity too, and vice versa — see `groupsStore.isParticipantMuted`.
const isMutedForMe = computed(() => groupsStore.isParticipantMuted(props.identity))

function updateMicVolume(percent: number) {
  micVolume.value = percent
  groupsStore.setParticipantVolume(props.identity, 'mic', percent)
}

function updateScreenShareVolume(percent: number) {
  screenShareVolume.value = percent
  groupsStore.setParticipantVolume(props.identity, 'screenShare', percent)
}

function updateMutedForMe(muted: boolean) {
  groupsStore.setParticipantMuted(props.identity, muted)
}

// Only while their screen share is actually being watched ("Смотреть стрим", see CallTile.vue) —
// stops it the same way as the tile's own button: unsubscribes, back to the "watch" prompt.
const isWatchingScreenShare = computed(() => groupsStore.isScreenShareWatched(props.identity))

function stopWatchingScreenShare() {
  groupsStore.setScreenShareWatched(props.identity, false)
}
</script>

<template>
  <ContextMenuContent class="w-64 rounded-xl [&_[role^=menuitem]]:rounded-lg">
    <ContextMenuLabel class="text-xs font-normal text-muted-foreground">
      {{ name }}
    </ContextMenuLabel>
    <ContextMenuSeparator />
    <div class="px-2 py-2">
      <div class="flex items-center justify-between mb-2">
        <Label class="text-[10px] uppercase text-muted-foreground font-bold">Громкость пользователя</Label>
      </div>
      <Slider
        :model-value="[micVolume]"
        :min="0"
        :max="200"
        :step="1"
        @update:model-value="(val) => val?.[0] !== undefined && updateMicVolume(val[0])"
      />
    </div>
    <ContextMenuSeparator />
    <div class="px-2 py-2">
      <div class="flex items-center justify-between mb-2">
        <Label class="text-[10px] uppercase text-muted-foreground font-bold">Громкость демонстрации экрана</Label>
      </div>
      <Slider
        :model-value="[screenShareVolume]"
        :min="0"
        :max="200"
        :step="1"
        @update:model-value="(val) => val?.[0] !== undefined && updateScreenShareVolume(val[0])"
      />
    </div>
    <ContextMenuSeparator />
    <!-- A plain item (no checkbox indent), same as "Прекратить просмотр" below; the label says what
         a click will do. -->
    <ContextMenuItem @select="updateMutedForMe(!isMutedForMe)">
      {{ isMutedForMe ? 'Включить звук' : 'Заглушить' }}
    </ContextMenuItem>
    <ContextMenuItem v-if="isWatchingScreenShare" @select="stopWatchingScreenShare">
      Прекратить просмотр
    </ContextMenuItem>
  </ContextMenuContent>
</template>
